/**
 * AreaChart - a filled cartesian chart with plain, stacked, and percent
 * variants.
 *
 * Built on recharts. Accepts arbitrary row shapes, infers its series when they
 * are not declared, and always resolves to a visible state (loading, error,
 * empty, or chart) so it can be dropped straight onto a dashboard without guard
 * code around it.
 *
 * Where the line chart's area variant is a line with a wash under it - the line
 * carries the data and the fill is atmosphere - this chart is about the filled
 * quantity itself, and stacks it. That is the reason it exists separately:
 * composition over time is a question a single line cannot answer.
 *
 * The value axis always includes zero and there is no option to move it. A fill
 * reads as the quantity between the baseline and the value, so a baseline
 * anywhere else restates every quantity on the chart. A bare line may frame its
 * range because it encodes change through position; a filled one may not.
 *
 * Usage:
 *   <AreaChart data={rows} xKey="month" />
 *   <AreaChart variant="stacked" data={rows} xKey="month" series={["open", "closed"]} />
 *   <AreaChart variant="percent" data={rows} xKey="quarter" />
 */

import { useCallback, useMemo, useState } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "../../ui/utils";
import {
  CHART_PALETTE_DARK,
  CHART_PALETTE_LIGHT,
  SURFACE_DARK,
  SURFACE_LIGHT,
  AXIS_TICK_FONT_SIZE,
  AXIS_TICK_FONT_WEIGHT,
  GRID_DASH,
  MARK_FILL_OPACITY,
  MARK_STROKE_WIDTH,
  areaGlow,
  formatCompact,
  formatNumber,
  resolveErrorMessage,
  resolvePlotHeight,
  useElementWidth,
  useIsDarkTheme,
  usePrefersReducedMotion,
} from "../chartUtils";
import {
  ChartEmptyState,
  ChartErrorBoundary,
  ChartErrorState,
  ChartLoadingState,
  ChartWarnings,
} from "../ChartStates";
import { hasNegativeValues } from "../cartesianUtils";
import { AreaTooltip, SeriesLegend, SeriesTable } from "./AreaChrome";
import {
  normalizeAreaData,
  type AreaPoint,
  type AreaVariant,
  type BandOrder,
  type ChartSeries,
  type PointSort,
  type SeriesInput,
} from "./areaUtils";

/** Hairlines one step off the surface, so the grid never competes with data. */
const GRID_LIGHT = "#e2e8f0";
const GRID_DARK = "rgba(255,255,255,0.10)";
const AXIS_TEXT_LIGHT = "#64748b";
const AXIS_TEXT_DARK = "rgba(255,255,255,0.55)";

/** Opacity a hidden-by-hover series drops to, still visible but clearly behind. */
const DIMMED_FILL_OPACITY = 0.06;

export interface AreaChartProps {
  /**
   * DOM id for the chart's root element. Stays on the same element across the
   * loading, error, empty, and plotted states, so anything anchored to it does
   * not lose its target when the state changes. Also seeds the ids of the
   * internal title and plot nodes.
   */
  id?: string;
  /**
   * Identifier for the chart instance, used for tests, analytics, and event
   * handlers. Rendered as `data-name`, since `name` is not a valid attribute on
   * a div, and passed back as the last argument to onPointClick.
   */
  name?: string;

  /** Rows to plot. Null, undefined, and non-arrays resolve to the empty state. */
  data: unknown;
  /**
   * Plain areas sit on the baseline and may overlap, which compares each series
   * against zero. Stacked bands sit on each other, which compares the total and
   * costs the reader the ability to read any band but the bottom one exactly.
   * Percent restates that stack as a share of each point's own total, trading
   * the magnitude away for the composition.
   */
  variant?: AreaVariant;
  /** Edge shape. Step suits discrete state changes; monotone smooths trends. */
  curve?: "linear" | "monotone" | "step";
  /** Which series lands on the baseline of a stack, where it can be read exactly. */
  bandOrder?: BandOrder;

  /** Field holding each row's x-axis label. */
  xKey?: string;
  /**
   * Measures to plot. A bare string is shorthand for the data key. Omitted, the
   * numeric fields in the data are inferred, in first-seen order.
   */
  series?: SeriesInput[];

  title?: string;
  subtitle?: string;

  /** Plot height in px, excluding the header, legend, and table. */
  height?: number;
  /** Replaces the built-in categorical hues. Assigned in order, never cycled. */
  palette?: readonly string[];

  loading?: boolean;
  /** Fetch failure. Takes precedence over every other state. */
  error?: string | Error | null;
  /** Retry handler shown on the error state. */
  onRetry?: () => void;
  emptyMessage?: string;

  /** Series past this cap are dropped and named in a warning. */
  maxSeries?: number;
  /** Reorder points by their x value. Defaults to the order supplied. */
  sort?: PointSort;

  showLegend?: boolean;
  legendPosition?: "top" | "bottom";
  /** Allow clicking legend items to hide and restore series. */
  legendToggle?: boolean;

  showGrid?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;

  /**
   * Point markers. Defaults to automatic: shown on a plain area with few points,
   * and off on a stack, where they would sit on the seam between two bands and
   * read as belonging to neither.
   */
  showDots?: boolean;

  showTooltip?: boolean;
  /** Expandable text equivalent of the chart, quoting the values as supplied. */
  showDataTable?: boolean;
  /** Surface notes about dropped rows, stacked zeros, and uncharted series. */
  showWarnings?: boolean;

  /** Suffix appended to every quantity, for example "%" or " ms". Percent supplies its own. */
  unit?: string;
  valueFormatter?: (value: number) => string;
  /** Formats x-axis tick labels, for example shortening a date. */
  xFormatter?: (value: string) => string;

  onPointClick?: (point: AreaPoint, index: number, name?: string) => void;

  className?: string;
  /** Overrides the generated chart description read by screen readers. */
  ariaLabel?: string;
}

function AreaChartInner({
  id,
  name,
  data,
  variant = "area",
  curve = "monotone",
  bandOrder = "largest",
  xKey = "x",
  series: seriesInput,
  title,
  subtitle,
  height = 260,
  palette,
  loading = false,
  error = null,
  onRetry,
  emptyMessage = "There are no records to chart for the current selection.",
  maxSeries = 8,
  sort = "none",
  showLegend = true,
  legendPosition = "top",
  legendToggle = true,
  showGrid = true,
  showXAxis = true,
  showYAxis = true,
  showDots,
  showTooltip = true,
  showDataTable = false,
  showWarnings = true,
  unit = "",
  valueFormatter,
  xFormatter,
  onPointClick,
  className,
  ariaLabel,
}: AreaChartProps) {
  const isDark = useIsDarkTheme();
  const reduceMotion = usePrefersReducedMotion();
  const [containerRef, containerWidth] = useElementWidth<HTMLDivElement>();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => new Set());
  const [tableOpen, setTableOpen] = useState(false);

  const plotHeight = resolvePlotHeight(height, 260);
  const isPercent = variant === "percent";
  const stacks = variant !== "area";

  const activePalette = useMemo(() => {
    const source = palette?.filter?.(Boolean);
    if (source?.length) return source;
    return isDark ? CHART_PALETTE_DARK : CHART_PALETTE_LIGHT;
  }, [palette, isDark]);

  const formatValue = useCallback(
    (value: number) => {
      if (typeof valueFormatter === "function") {
        try {
          return valueFormatter(value);
        } catch {
          // A throwing formatter must not take the chart down with it.
          return formatNumber(value);
        }
      }
      return formatNumber(value);
    },
    [valueFormatter],
  );

  // What is drawn on a percent chart is a share, which the caller's value
  // formatter was never written for: it formats their quantity.
  const formatPlotted = useCallback(
    (value: number) =>
      isPercent
        ? `${Number.isFinite(value) ? Math.round(value * 10) / 10 : 0}`
        : formatValue(value),
    [isPercent, formatValue],
  );

  const formatTick = useCallback(
    (value: string) => {
      if (typeof xFormatter !== "function") return value;
      try {
        return xFormatter(value);
      } catch {
        return value;
      }
    },
    [xFormatter],
  );

  const { points, rawPoints, series, warnings } = useMemo(
    () =>
      normalizeAreaData(data, {
        xKey,
        series: seriesInput,
        palette: activePalette,
        maxSeries,
        sort,
        variant,
        bandOrder,
      }),
    [data, xKey, seriesInput, activePalette, maxSeries, sort, variant, bandOrder],
  );

  // Hidden series leave the plot but keep their assigned colour, so restoring
  // one does not repaint the survivors.
  const visibleSeries = useMemo(
    () => series.filter((entry) => !hiddenIds.has(entry.id)),
    [series, hiddenIds],
  );


  const handleToggle = useCallback(
    (seriesId: string) => {
      if (!legendToggle) return;
      setHiddenIds((prev) => {
        const next = new Set(prev);
        if (next.has(seriesId)) {
          next.delete(seriesId);
          return next;
        }
        // Refuse the toggle that would empty the chart.
        if (prev.size + 1 >= series.length) return prev;
        next.add(seriesId);
        return next;
      });
    },
    [legendToggle, series.length],
  );

  const handlePointClick = useCallback(
    // recharts hands the click through as its own chart state object.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => {
      if (typeof onPointClick !== "function") return;
      const index = state?.activeTooltipIndex;
      // The originals are what a caller wants back, not the stacked copy.
      const point = typeof index === "number" ? rawPoints?.[index] : undefined;
      if (point) onPointClick(point, index, name);
    },
    [onPointClick, rawPoints, name],
  );

  // Dots on a seam belong to neither band, so a stack goes without them.
  const dotsOn = showDots ?? (!stacks && points.length <= 12);

  const negatives = useMemo(
    () => !stacks && hasNegativeValues(points, visibleSeries),
    [stacks, points, visibleSeries],
  );

  const effectiveUnit = isPercent ? "%" : unit;

  const description = useMemo(() => {
    if (ariaLabel) return ariaLabel;
    const kind = isPercent ? "Percent stacked area" : stacks ? "Stacked area" : "Area";
    const base = title ? `${kind} chart: ${title}.` : `${kind} chart.`;
    if (!visibleSeries.length || !points.length) return `${base} No data.`;
    const range =
      points.length > 1 ? ` from ${points[0]?.x} to ${points[points.length - 1]?.x}` : "";
    const parts = visibleSeries
      .map((entry) => {
        const values = points
          .map((point) => point[entry.key])
          .filter((value): value is number => typeof value === "number");
        if (!values.length) return `${entry.label} has no values`;
        const first = values[0];
        const last = values[values.length - 1];
        return `${entry.label} from ${formatPlotted(first)}${effectiveUnit} to ${formatPlotted(last)}${effectiveUnit}`;
      })
      .join(". ");
    return `${base} ${points.length} points${range}. ${parts}.`;
  }, [
    ariaLabel,
    isPercent,
    stacks,
    title,
    visibleSeries,
    points,
    effectiveUnit,
    formatPlotted,
  ]);

  const errorMessage = resolveErrorMessage(error);
  const gridColor = isDark ? GRID_DARK : GRID_LIGHT;
  const axisTextColor = isDark ? AXIS_TEXT_DARK : AXIS_TEXT_LIGHT;
  const surface = isDark ? SURFACE_DARK : SURFACE_LIGHT;
  // Below this the axis labels crowd into each other.
  const compactAxis = containerWidth > 0 && containerWidth < 380;

  const header = (title || subtitle) && (
    <div className="mb-3 min-w-0">
      {title && (
        <h3
          id={id ? `${id}-title` : undefined}
          className="break-words text-sm font-bold leading-snug text-foreground"
        >
          {title}
        </h3>
      )}
      {subtitle && (
        <p className="mt-0.5 break-words text-xs leading-snug text-muted-foreground">{subtitle}</p>
      )}
    </div>
  );

  const legend = showLegend && series.length > 1 && (
    <SeriesLegend
      series={stacks ? [...series].reverse() : series}
      activeId={activeId}
      hiddenIds={hiddenIds}
      interactive={legendToggle}
      // A band is a filled block, so its key is one too.
      swatch={stacks ? "bar" : "line"}
      onActivate={setActiveId}
      onToggle={handleToggle}
    />
  );

  /* Error and loading short-circuit the plot but keep the header in place, so
     the surrounding layout never jumps or blanks between states. */
  if (errorMessage) {
    return (
      <div id={id} data-name={name} ref={containerRef} className={cn("w-full min-w-0", className)}>
        {header}
        <ChartErrorState height={plotHeight} message={errorMessage} onRetry={onRetry} />
      </div>
    );
  }

  if (loading) {
    return (
      <div id={id} data-name={name} ref={containerRef} className={cn("w-full min-w-0", className)}>
        {header}
        <ChartLoadingState height={plotHeight} shape="cartesian" />
      </div>
    );
  }

  if (!points.length || !visibleSeries.length) {
    return (
      <div id={id} data-name={name} ref={containerRef} className={cn("w-full min-w-0", className)}>
        {header}
        <ChartEmptyState height={plotHeight} message={emptyMessage} shape="cartesian" />
        {showWarnings && <ChartWarnings warnings={warnings} />}
      </div>
    );
  }

  /* A fill states the quantity between the baseline and the value, so the axis
     has to include zero. Percent is a closed scale and gets its exact range. */
  const valueDomain: [number, number] | [(min: number) => number, (max: number) => number] =
    isPercent ? [0, 100] : [(min: number) => Math.min(0, min), (max: number) => Math.max(0, max)];

  return (
    <div id={id} data-name={name} ref={containerRef} className={cn("w-full min-w-0", className)}>
      {header}

      {legendPosition === "top" && legend && <div className="mb-2">{legend}</div>}

      <div
        id={id ? `${id}-plot` : undefined}
        className="w-full"
        style={{ height: plotHeight }}
        role="img"
        aria-label={description}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={points}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            onClick={onPointClick ? handlePointClick : undefined}
          >
            {showGrid && (
              // Dashed hairline, horizontal only: a gridline parallel to the
              // trend measures nothing, and dashes keep it behind the data.
              <CartesianGrid
                stroke={gridColor}
                strokeWidth={1}
                strokeDasharray={GRID_DASH}
                vertical={false}
              />
            )}

            {showXAxis && (
              <XAxis
                dataKey="x"
                tick={{
                  fill: axisTextColor,
                  fontSize: AXIS_TICK_FONT_SIZE,
                  fontWeight: AXIS_TICK_FONT_WEIGHT,
                }}
                tickFormatter={formatTick}
                axisLine={{ stroke: gridColor }}
                tickLine={false}
                minTickGap={compactAxis ? 24 : 12}
                interval="preserveStartEnd"
                height={28}
              />
            )}

            {showYAxis && (
              <YAxis
                tick={{
                  fill: axisTextColor,
                  fontSize: AXIS_TICK_FONT_SIZE,
                  fontWeight: AXIS_TICK_FONT_WEIGHT,
                }}
                // Compact ticks keep the axis narrow without hiding magnitude.
                tickFormatter={(value: number) =>
                  isPercent ? `${value}%` : formatCompact(value)
                }
                axisLine={false}
                tickLine={false}
                width={compactAxis ? 36 : 46}
                domain={valueDomain}
                // A closed scale is only readable if it shows it is closed.
                ticks={isPercent ? [0, 25, 50, 75, 100] : undefined}
              />
            )}

            {/* With values on both sides of zero the baseline is a real
                boundary between fills, so it is drawn rather than left implied. */}
            {negatives && <ReferenceLine y={0} stroke={axisTextColor} strokeWidth={1} />}

            {showTooltip && (
              <Tooltip
                content={
                  <AreaTooltip
                    series={visibleSeries}
                    unit={effectiveUnit}
                    stacked={stacks}
                    showTotal={stacks}
                    // Only a percent chart plots something other than the
                    // quantity, so only it needs the originals alongside.
                    rawPoints={isPercent ? rawPoints : undefined}
                    valueFormatter={formatValue}
                    plottedFormatter={formatPlotted}
                  />
                }
                // The crosshair is the reading aid on a cartesian plot.
                cursor={{ stroke: gridColor, strokeWidth: 1 }}
                wrapperStyle={{ outline: "none", zIndex: 20 }}
                isAnimationActive={!reduceMotion}
              />
            )}

            {visibleSeries.map((entry) => {
              const dimmed = activeId != null && activeId !== entry.id;
              return (
                <Area
                  key={entry.id}
                  dataKey={entry.key}
                  name={entry.label}
                  stackId={stacks ? "stack" : undefined}
                  type={curve === "step" ? "stepAfter" : curve}
                  // Outline treatment: a translucent fill in the series hue
                  // closed by a solid stroke of the same colour. Stacked bands
                  // get it too, so a stack reads as the sum of its outlined
                  // parts rather than a solid column.
                  stroke={entry.color}
                  strokeWidth={MARK_STROKE_WIDTH}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill={entry.color}
                  fillOpacity={dimmed ? DIMMED_FILL_OPACITY : MARK_FILL_OPACITY}
                  opacity={dimmed ? 0.35 : 1}
                  // The FILLED-mark glow, not the outline one: an area is a
                  // large silhouette, so the bar/donut glow haloes the whole
                  // band and this plot ends up hazier than every chart beside
                  // it. Dropped entirely while dimmed.
                  style={dimmed ? undefined : { filter: areaGlow(entry.color) }}
                  // On a plain area a gap is data and is never bridged. A stack
                  // has no gaps left to bridge: they were counted as zero and
                  // reported, because a hole would restate every band above it.
                  connectNulls={false}
                  isAnimationActive={!reduceMotion}
                  animationDuration={600}
                  // The ring keeps a marker legible where edges cross.
                  dot={dotsOn ? { r: 3, fill: entry.color, stroke: surface, strokeWidth: 2 } : false}
                  activeDot={
                    stacks ? false : { r: 4.5, fill: entry.color, stroke: surface, strokeWidth: 2 }
                  }
                  className={onPointClick ? "cursor-pointer" : undefined}
                />
              );
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {legendPosition === "bottom" && legend && <div className="mt-2">{legend}</div>}

      {showWarnings && <ChartWarnings warnings={warnings} />}

      {showDataTable && (
        <SeriesTable
          // The table quotes what was supplied, not the stack-safe copy: a
          // value that was missing has to still read as missing here.
          points={rawPoints}
          series={visibleSeries}
          unit={unit}
          xHeader={xKey === "x" ? "Point" : xKey}
          open={tableOpen}
          valueFormatter={formatValue}
          onToggle={() => setTableOpen((open) => !open)}
        />
      )}
    </div>
  );
}

/**
 * Public entry point. Wraps the chart in a local error boundary so a render
 * crash stays inside the chart box instead of reaching the app-level boundary
 * and blanking the page.
 */
export function AreaChart(props: AreaChartProps) {
  const plotHeight = resolvePlotHeight(props?.height, 260);

  return (
    <ChartErrorBoundary height={plotHeight} onRetry={props?.onRetry}>
      <AreaChartInner {...props} />
    </ChartErrorBoundary>
  );
}

export default AreaChart;

export type { AreaPoint, AreaVariant, BandOrder, ChartSeries, PointSort, SeriesInput };
